/**
 * 붙여넣은 HTML 을 편집기에 넣을 꼴로 고치고, 저장할 꼴로 되돌리는 **순수 변환**.
 *
 * tiptap 을 부르지 않는다 — DOM 파서만 쓴다. 그래서 편집기를 띄우지 않고 단독으로 시험된다.
 *
 * ## 왜 붙여넣기 «전»에 HTML 을 고쳐야 하나 (2026-09-23 실측)
 *
 * tiptap 3.29.2 의 `Image` 는 기본값이 `allowBase64: false` 라, `data:` 그림을 **파서가
 * 통째로 버린다.** 실측:
 *
 * ```
 * IN : <p><img src="data:image/png;base64,iVBORw0KGgo=" alt="d"></p>
 * OUT: <p></p>            ← 그림이 «없어졌다»
 * ```
 *
 * 그래서 「넣고 나서 고친다」는 안 된다 — 넣는 순간 이미 사라져 고칠 대상이 없다.
 * 붙여넣기를 가로채 **먼저 `src` 를 갈아 놓고** tiptap 에 넘긴다. 나머지 서식(굵게·표·
 * 목록·링크…)은 tiptap 의 파서가 그대로 살린다 — Ⓐ 「양식 그대로」.
 */

import {
  IMAGE_UPLOADING,
  imageFailedMessage,
  IMAGE_FAILED_REASON_DEFAULT,
} from './editor-messages';
import { planPastedImages, type PastedImagePlan } from './pasted-images';
import { sanitizeBoardHtml } from '../../lib/sanitize';

/**
 * 「올리는 중」 자리표시의 주소 앞머리. **앱 상대 주소**다 —
 * 바깥으로 요청이 나가지 않고, tiptap 의 `img[src]` 파스 규칙도 통과한다.
 */
export const UPLOADING_SRC_PREFIX = '/__axi-image-uploading__';

/** 「올리지 못함」 표시의 주소. 지워지지 않는다 — 사용자가 손으로 지워야 한다. */
export const FAILED_SRC = '/__axi-image-failed__';

/** 올린 파일을 «오래 사는» 주소로 가리키는 속성. 저장할 때 `src` 를 이것으로 다시 만든다. */
export const FILE_ID_ATTR = 'data-file-id';

// ── 표 셀 가로 정렬 ────────────────────────────────────────────────────────
//
// 사용자 결정(2026-09-23): **표 «셀 가로 정렬»만 살린다 — 좁게. «열 폭»은 버린다.**
//
// ⛔ 그러려고 `style` 을 열지 않는다. tiptap 은 정렬을 `style="text-align: …"` 로 내는데
//   (`@tiptap/extension-table@3.29.2` `dist/index.js:24-31` 의 `createAlignAttribute`),
//   `style` 을 한 번 허용하면 「이미 열려 있으니 하나 더」로 넓어진다. 지금의
//   **「저장 HTML 에 `style` 0건」**은 지킬 값이 있는 성질이다.
//   ⇒ 저장 직전에 그 값을 **`align` 속성으로 «옮긴다»**. `align` 은 이미 허용목록에 있다
//     (`lib/sanitize.ts` 의 `ALLOWED_ATTR`).
//
// 열 폭(`<colgroup><col style="min-width">` · `<table style="min-width">`)은 그대로 버린다.

/**
 * 받는 값 셋. **tiptap 이 실제로 받는 것이 이 셋이다** — `justify` 는 «없다».
 *
 * 근거(소스 실측): `@tiptap/extension-table@3.29.2` `dist/index.js:5-10`
 * `normalizeTableCellAlign` 이 `left`·`right`·`center` 만 통과시키고 나머지는 `null` 이다.
 * 그래서 `align="justify"` 도 `style="text-align:justify"` 도 편집기에 들어오지 못한다
 * (실측으로도 확인: 둘 다 노드 attr 이 `align: null`).
 *
 * ⚠⚠ **서버 허용목록에는 `justify` 가 «열려 있다» — 우리는 그 값을 «내지 않는다».**
 *   서버(PR #1656)가 `td`/`th` 의 `align` 에 넷(`left|center|right|justify`)을 열었는데,
 *   그것은 2026-09-23 에 관제가 「넷」으로 넘긴 것이고 **위 실측이 셋임을 보인 뒤에 정정됐다.**
 *   무해하다 — sanitize 는 「**들어올 수 있는** 것」을 다루고 이 목록은 「우리가 **내놓는**
 *   것」이라, 서버 ⊇ 우리면 된다. 다만 **우리가 안 내는 값이 허용목록에 한 칸 남아 있다.**
 *   언젠가 서버 목록을 좁힐 때 「이 칸은 왜 열려 있지」가 될 것이므로 까닭을 여기 적어 둔다:
 *   **`justify` 를 내는 경로가 우리에게 없다.** 좁혀도 우리 출력은 변하지 않는다.
 */
export const CELL_ALIGN_VALUES = ['left', 'center', 'right'] as const;
export type CellAlign = (typeof CELL_ALIGN_VALUES)[number];

/**
 * ⚠⚠ **읽기 화면을 만드는 사람에게 — `align` 은 CSS 로 «그려야» 한다.**
 *
 * `align` 은 HTML5 에서 **폐기된 속성**이다. 브라우저가 기본 스타일로 그려 줄 수도,
 * **안 그려 줄 수도** 있다. 우리는 **실제 브라우저에서 안 쟀다**(2026-09-23 · 이 세션에
 * 브라우저가 없다). 잰 것은 하나뿐이고 그것은 나쁜 소식이다:
 *
 *     jsdom: getComputedStyle(td[align="right"]).textAlign === ''   ← 매핑하지 «않는다»
 *
 * ⇒ 속성만 옮겨 두고 「그려질 것이다」로 두면 **조용히 안 그려진다.** 저장된 본문을 그리는
 *   화면은 아래 선택자 셋을 **자기 CSS 에 명시해야 한다.** 편집기는 이미 넣어 두었다
 *   (`RichTextEditor.css` — `editor-css-sentinels.test.ts` 가 잠근다).
 */
export const REQUIRED_CELL_ALIGN_SELECTORS: readonly string[] = CELL_ALIGN_VALUES.map(
  (v) => `:is(th, td)[align="${v}"]`,
);

/**
 * 이 칸의 가로 정렬. `style` 을 먼저 보고 없으면 `align` 을 본다 —
 * **tiptap 의 `parseAlign` 과 같은 순서**(`dist/index.js:11-16`). 저장한 글을 다시
 * 편집기에 넣었을 때 판정이 갈리지 않게 한다.
 */
export function cellAlignOf(el: Element): CellAlign | null {
  const styleAlign = (el as HTMLElement).style?.textAlign?.trim().toLowerCase() ?? '';
  const attrAlign = (el.getAttribute('align') ?? '').trim().toLowerCase();
  const value = styleAlign || attrAlign;
  return (CELL_ALIGN_VALUES as readonly string[]).includes(value) ? (value as CellAlign) : null;
}

/**
 * `th`·`td` 의 가로 정렬을 `style` → `align` 으로 **옮긴다**. 옮긴 칸 수를 돌려준다.
 *
 * - 받는 값 밖이면(`justify`·`middle`·`end` …) **`align` 을 지운다** — 서버도 셋만 받으므로
 *   남겨 두면 서버가 지우고, 그러면 「우리는 보냈는데 저장은 안 된」 상태가 된다.
 * - `style` 은 **여기서 지운다.** sanitize 가 어차피 지우지만, 그러면 「저장 HTML 에 style
 *   0건」이 **허용목록 설정에만** 매달린다 — 누가 `style` 을 허용목록에 넣는 순간 셀의
 *   `text-align` 이 되살아나 그 성질이 조용히 깨진다. 그래서 여기서도 지우고,
 *   **`editor-html.test.ts` 가 이 함수를 직접 불러** 그것을 잠근다(RED 확인: 이 줄을 빼면
 *   그 시험만 빨개진다 — `serializeForSave` 쪽 시험은 sanitize 때문에 전부 초록이었다).
 */
export function promoteCellAlign(root: ParentNode): number {
  let moved = 0;
  for (const el of Array.from(root.querySelectorAll('th, td'))) {
    const align = cellAlignOf(el);
    if (align) {
      el.setAttribute('align', align);
      moved += 1;
    } else {
      el.removeAttribute('align');
    }
    el.removeAttribute('style');
  }
  return moved;
}

/**
 * 저장되는 그림 주소 — **서버에 이 라우트가 «있다».**
 *
 * `apps/chat-server/src/modules/board/board-image.routes.ts` 의 `'/api/v1/files/:id/image'`
 * (`board.routes.ts` 의 `registerBoardImageRoutes` 가 등록한다). 그 라우트는 `ETag` 를 주고
 * **`If-None-Match` 를 보아 304 를 낸다** — 한 글에 그림이 여럿이면 값이 크다.
 *
 * ⚠⚠ **2026-09-23 에 나는 이 라우트가 «없다»고 적었다. 틀렸다.**
 *   `modules/files/` 만 grep 했기 때문이다 — 그 라우트는 **`modules/board/` 에 있고 «경로»만
 *   `/api/v1/files/...`** 다. **경로 앞머리와 모듈 폴더가 다르다.**
 *   ⇒ 경로를 찾을 때는 **폴더를 가정하지 말고 «경로 문자열»로 전수 grep** 한다.
 *
 * 본문에 **presigned 주소를 적어 둘 수는 없다** — 서명이 박히고 한 시간 뒤 죽는다
 * (`lib/minio.ts` 의 `PRESIGN_TTL_SEC` — 기본 3600). 글은 몇 년 남는다. 그래서 저장하는 `src` 는
 * **앱 상대 · 만료 없는** 이 꼴이고, 그 덕에 **모바일·메일도 저장 HTML 을 그대로 그릴 수 있다.**
 *
 * 바꿀 일이 생기면 **여기 한 줄만** 고친다 —
 * `content-length.server-contract.test.ts` 가 서버 라우트 선언과 맞춰 본다.
 */
export function boardImageSrc(fileId: string): string {
  return `/api/v1/files/${encodeURIComponent(fileId)}/image`;
}

/** 아직 올리는 중인 자리표시 주소(토큰 포함). */
export function uploadingSrc(token: string): string {
  return `${UPLOADING_SRC_PREFIX}?t=${encodeURIComponent(token)}`;
}

export function isUploadingSrc(src: string): boolean {
  return src.startsWith(UPLOADING_SRC_PREFIX);
}

export function isFailedSrc(src: string): boolean {
  return src === FAILED_SRC;
}

let tokenSeq = 0;
/** 자리표시를 나중에 다시 찾기 위한 표. 한 문서 안에서만 유일하면 된다. */
export function nextUploadToken(): string {
  tokenSeq += 1;
  return `u${Date.now().toString(36)}-${tokenSeq}`;
}

export interface PendingUpload {
  token: string;
  file: File;
  /** 원래 `alt` — 올리기가 끝나면 되돌려 준다. */
  alt: string;
}

export interface RewrittenPaste {
  html: string;
  uploads: PendingUpload[];
  /** 이 붙여넣기에서 곧바로 실패로 판정된 그림 수(바깥 주소·해독 불가). */
  failedCount: number;
}

/**
 * 붙여넣은 HTML → 편집기에 넣을 HTML + 올릴 것 목록.
 *
 * `<img>` 를 **직접 훑으므로** 판정과 대상이 어긋나지 않는다(빈 `src` 하나 때문에 번호가
 * 밀려 다른 그림에 다른 판정이 붙던 함정을 피한다).
 */
export function rewritePastedHtml(
  html: string,
  makeToken: () => string = nextUploadToken,
): RewrittenPaste {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const imgs = Array.from(doc.querySelectorAll('img'));
  const uploads: PendingUpload[] = [];
  let failedCount = 0;

  for (const el of imgs) {
    const src = el.getAttribute('src') ?? '';
    const alt = el.getAttribute('alt') ?? '';
    if (src.length === 0) {
      // 주소가 없는 `<img>` — 보여 줄 것이 없다. 조용히 지우지 않고 실패로 표시한다.
      markFailed(el, IMAGE_FAILED_REASON_DEFAULT);
      failedCount += 1;
      continue;
    }
    const [plan] = planPastedImages([src]) as [PastedImagePlan];
    if (plan.kind === 'keep') continue;
    if (plan.kind === 'fail') {
      markFailed(el, plan.reason);
      failedCount += 1;
      continue;
    }
    const token = makeToken();
    el.setAttribute('src', uploadingSrc(token));
    el.setAttribute('alt', IMAGE_UPLOADING);
    // ⚠ 크기는 그대로 둔다 — tiptap 의 image 노드가 width/height 를 가진다(실측).
    uploads.push({ token, file: plan.file, alt });
  }

  return { html: doc.body.innerHTML, uploads, failedCount };
}

function markFailed(el: Element, reason: string): void {
  el.setAttribute('src', FAILED_SRC);
  el.setAttribute('alt', imageFailedMessage(reason));
  // 바깥 주소를 «흔적으로도» 남기지 않는다 — 크기까지 걷어낸다.
  el.removeAttribute('title');
  el.removeAttribute('srcset');
  el.removeAttribute(FILE_ID_ATTR);
}

/**
 * 옛 글(`contentType` 이 `'TEXT'`)을 **에디터에 열기 위한** HTML 로 바꾼다.
 *
 * ⛔ 이것이 없으면 **옛 글을 고칠 때 줄바꿈이 사라진다.** 글자 본문을 그대로 tiptap 에 주면
 *   HTML 로 파스되어 개행이 «공백»이 되고, 사용자는 「고치기만 눌렀는데 문단이 붙었다」를 본다.
 *
 * - 꺽쇠·앰퍼샌드를 **글자로** escape 한다 — 옛 글에 `<b>` 가 «글자로» 적혀 있으면
 *   그것은 서식이 아니라 글자였다(TEXT 로 그려 왔다). 서식으로 바꿔 주면 뜻이 달라진다.
 * - 빈 줄로 문단을 가르고, 문단 안의 개행은 `<br>` 로 둔다 — 화면에서 보던 모양과 같다.
 */
export function textToEditorHtml(text: string): string {
  const esc = (s: string) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const paras = text.replace(/\r\n?/g, '\n').split(/\n{2,}/);
  const body = paras
    .map((p) => `<p>${esc(p).split('\n').join('<br>')}</p>`)
    .join('');
  return body.length > 0 ? body : '<p></p>';
}

/**
 * 에디터가 낸 HTML 이 **사실상 비었는가**.
 *
 * ⛔ 빈 에디터는 `''` 가 아니라 `<p></p>` 를 낸다. 글자 길이로만 보면 「내용이 있다」가 되어
 *   **빈 글이 저장된다**(`canSubmitForm` 이 그것에 속았다). 글자와 «보이는 것»을 함께 본다 —
 *   그림 하나만 있는 글은 글자가 0 이어도 «빈 것이 아니다».
 */
export function htmlIsBlank(html: string): boolean {
  if (typeof html !== 'string' || html.trim().length === 0) return true;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (doc.body.querySelector('img, table, hr')) return false;
  return (doc.body.textContent ?? '').replace(/ /g, ' ').trim().length === 0;
}

export interface SerializedForSave {
  html: string;
  /** 올리지 못한 그림 수. 0 이 아니면 저장을 막고 사용자에게 알린다. */
  failedCount: number;
  /** 아직 올리는 중인 그림 수. 이것도 0 이어야 저장할 수 있다. */
  uploadingCount: number;
}

/**
 * 편집기 HTML → 저장할 HTML.
 *
 * - `data-file-id` 가 붙은 그림은 `src` 를 **만료 없는 주소**로 다시 만든다
 *   (편집 중에는 presigned 주소로 보여 주었다 — 그것을 저장하면 한 시간 뒤 깨진다).
 * - 자리표시·실패 표시는 **세어서 돌려준다.** 지우지 않는다 — 사용자가 무엇을 잃었는지
 *   알아야 하고, 저장은 막아야 한다.
 * - 표 셀 가로 정렬은 `style` → `align` 으로 **옮긴다**(`promoteCellAlign`). 열 폭은 버린다.
 * - 끝으로 `sanitizeBoardHtml` 을 한 번 거친다(두 겹 중 앞 겹 — 뒤 겹은 서버 몫이고
 *   **아직 없다**).
 */
export function serializeForSave(html: string): SerializedForSave {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let failedCount = 0;
  let uploadingCount = 0;

  // 사용자 결정(2026-09-23) — 셀 정렬만 살린다. `style` 은 열지 않고 `align` 으로 옮긴다.
  promoteCellAlign(doc.body);

  for (const el of Array.from(doc.querySelectorAll('img'))) {
    const src = el.getAttribute('src') ?? '';
    const fileId = el.getAttribute(FILE_ID_ATTR);
    if (fileId) {
      el.setAttribute('src', boardImageSrc(fileId));
      continue;
    }
    if (isUploadingSrc(src)) { uploadingCount += 1; continue; }
    if (isFailedSrc(src)) { failedCount += 1; continue; }
    if (src.startsWith('/')) continue;
    // 우리 주소도 아니고 파일 아이디도 없다 — 저장하면 안 되는 그림이다.
    markFailed(el, IMAGE_FAILED_REASON_DEFAULT);
    failedCount += 1;
  }

  return { html: sanitizeBoardHtml(doc.body.innerHTML), failedCount, uploadingCount };
}
